import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { KibContainer } from '@chewy/kib-layout-react'
import { NumberUtils } from '@pbt/pbt-ui-components'

import type {
  CompletedSaleTransaction,
  FailedSaleTransaction,
} from '~/api/graphql/generated/types'
import arrowImage from '~/components/common/images/arrowPaymentRequest.svg'
import catLaptopImage from '~/components/common/images/catLaptopPaymentRequest.svg'
import dogTerminalImage from '~/components/common/images/dogTerminalPaymentRequest.svg'
import { usePaymentTransactionStatus } from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/hooks/usePaymentTransactionStatus'
import {
  isTransactionCompleted,
  isTransactionError,
} from '~/constants/addPaymentDialogV2Constants'

import { usePaymentDialog } from '../context/PaymentDialogProvider'
import { accountCreditsSelected } from '../utils/clientAccountCreditUtils'

import '@chewy/kib-layout-styles/dist/styles/kib-container.css'
import styles from './PaymentRequestScreen.module.scss'

export const PaymentRequestScreen = () => {
  const { t } = useTranslation('Dialogs')
  const { dialogState, actions } = usePaymentDialog()
  const { paymentData } = dialogState
  const amountToCharge = parseFloat(paymentData.amountToCharge || '0')
  const { transaction, error } = usePaymentTransactionStatus(
    dialogState.paymentData.transactionId || '',
  )

  useEffect(() => {
    if (paymentData.transactionId && transaction) {
      if (isTransactionCompleted(transaction)) {
        actions.handleTransactionSuccess(
          paymentData,
          transaction as CompletedSaleTransaction,
        )
      } else if (isTransactionError(transaction)) {
        actions.handleTransactionFailure(
          transaction as FailedSaleTransaction,
          accountCreditsSelected(
            paymentData.accountCreditTotal,
            paymentData.selectedAccountCreditIds,
          ),
        )
      }
    } else if (paymentData.transactionId && error) {
      actions.handleTransactionHardFailure(
        accountCreditsSelected(
          paymentData.accountCreditTotal,
          paymentData.selectedAccountCreditIds,
        ),
        error,
      )
    }
  }, [
    paymentData.transactionId,
    transaction,
    error,
    paymentData.accountCreditTotal,
    paymentData.selectedAccountCreditIds,
  ])

  return (
    <div className={styles.paymentRequestScreenContainer}>
      <KibContainer className={styles.paymentRequestContainer}>
        <div className={styles.paymentRequestInfo}>
          <div className={styles.infoText}>
            {t('Dialogs:ADD_PAYMENT_DIALOG_V2.PAYMENT_REQUEST_INFO', {
              amount: `${NumberUtils.formatMoney(amountToCharge)}`,
              terminal:
                paymentData.paymentTerminalDisplayName ||
                paymentData.paymentTerminal ||
                '',
            })}
          </div>
          <div className={styles.instructionText}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.LOOK_FOR_TRANSACTION_ON_TERMINAL',
            )}
          </div>
        </div>
        <div className={styles.paymentRequestImages}>
          <img
            alt={t('Dialogs:ADD_PAYMENT_DIALOG_V2.CAT_WITH_LAPTOP_ALT')}
            className={styles.paymentImage}
            src={catLaptopImage}
          />
          <img
            alt={t('Dialogs:ADD_PAYMENT_DIALOG_V2.ARROW_ALT')}
            className={styles.arrowImage}
            src={arrowImage}
          />
          <img
            alt={t('Dialogs:ADD_PAYMENT_DIALOG_V2.DOG_WITH_TERMINAL_ALT')}
            className={styles.paymentImage}
            src={dogTerminalImage}
          />
        </div>
      </KibContainer>
    </div>
  )
}
